import { Storage } from "aws-amplify";

import API from "../services/API";
import Analytics from "../services/Analytics";
import { uuidv4, getDownloadFileDate } from "./Utils";

/**
 * 리포트 다운로드 요청 격리 모듈.
 *
 * 호출부(모달/화면)는 requestReportDownload의 반환 status만 알고,
 * 호출·수신 방식(LS_sendReport + S3 동기 다운로드 / 메일 폴백)은 이 파일만 안다.
 * 향후 메일 폴백을 "다운로드 센터"(파일 준비완료 상태 표시 → 다운로드)로 대체할 때
 * 이 파일만 교체하면 된다.
 *
 * 서버 계약 (sales-server LS_sendReport, 세션 A):
 * - options(AWSJSON)에 클라 생성 uuid requestKey를 담아 호출한다.
 * - 반환 1 = 동기 생성 완료 → 웹이 key `{storeId}/excel/{requestKey}/{filename}.xlsx`를
 *   재구성해 Storage.get으로 즉시 다운로드 (메일 발송 없음).
 * - 반환 0 = 대용량 → 파일 준비 후 recepient로 메일 발송.
 * - 호출 에러/타임아웃 = 실패 → 메일이 발송된 것처럼 안내하면 안 된다.
 */

// 서버가 생성하는 item별 고정 파일명 (S3 key 재구성에 사용 — 서버 계약과 일치해야 함)
// todo: 2026-07-15 우진이 sales-server 세션에 아래 계약으로 확인 요청함 — 서버 회신 후 최종 확정
export const REPORT_FILE_NAMES = {
  logisticsHistories: "물류이동 리포트",
  lossDamageHistories: "손실파손 리포트",
};

export const REPORT_RESULT = {
  DOWNLOADED: "downloaded",
  EMAIL: "email",
  ERROR: "error",
};

/**
 * 리포트 생성을 요청하고, 동기 생성 완료 시 브라우저에서 즉시 다운로드한다.
 * @param {string} storeId
 * @param {string} item - 서버 리포트 item 이름 (예: "logisticsHistories", "lossDamageHistories")
 * @param {Array<{key: string, operation: string, value: any}>} filters
 * @param {string} recepient - 대용량 폴백용 수신 이메일 (항상 전달)
 * @returns {Promise<{status: "downloaded"|"email"|"error"}>}
 */
export const requestReportDownload = async (
  storeId,
  item,
  filters,
  recepient,
) => {
  const requestKey = uuidv4();
  try {
    const result = await API.sendReportToEmail(storeId, item, filters, recepient, {
      requestKey,
    });
    if (result === 1) {
      const fileName = REPORT_FILE_NAMES[item] || item;
      const excelKey = `${storeId}/excel/${requestKey}/${fileName}.xlsx`;
      const level = "public";
      const downloadObject = await Storage.get(excelKey, {
        download: true,
        level: level,
      });
      const blob = downloadObject.Body;
      const url = URL.createObjectURL(
        new Blob([blob.buffer], { type: downloadObject.contentType }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fileName}-${getDownloadFileDate()}.xlsx`;
      const clickHandler = () => {
        setTimeout(() => {
          URL.revokeObjectURL(url);
          a.removeEventListener("click", clickHandler);
        }, 100);
      };
      a.addEventListener("click", clickHandler, false);
      a.click();
      try {
        // 다운로드는 이미 완료됨 — 임시파일 정리 실패가 결과를 뒤집으면 안 된다
        await Storage.remove(excelKey, { level: level });
      } catch (removeError) {
        Analytics.sendErrorReport(removeError, "requestReportDownload.cleanup", {
          storeId,
          item,
        });
      }
      return { status: REPORT_RESULT.DOWNLOADED };
    }
    if (result === 0) {
      return { status: REPORT_RESULT.EMAIL };
    }
    // 계약 밖 반환값(null 등)은 실패로 처리 — 메일이 발송된 것처럼 안내하면 안 된다
    Analytics.sendErrorReport(
      new Error(`unexpected LS_sendReport result: ${result}`),
      "requestReportDownload",
      { storeId, item },
    );
    return { status: REPORT_RESULT.ERROR };
  } catch (e) {
    Analytics.sendErrorReport(e, "requestReportDownload", { storeId, item });
    return { status: REPORT_RESULT.ERROR };
  }
};
